import {
  AcknowledgementStatus,
  ApprovalDecision,
  ApprovalPolicy,
  DocumentPermissionFlag,
  DocumentVersionStatus,
  type SignableAct,
  type SignedCommand,
  type AccessRuleInput,
} from '@qualicloud/contracts';
import { WATERMARK_TEXT, requiresWatermarkOnExport } from '@qualicloud/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AccessRuleEditor } from '@/components/access-rule-editor';
import { ConfigurationWarnings } from '@/components/configuration-warnings';
import { SignatureDialog } from '@/components/signature/signature-dialog';
import { PrintableRecord, SignatureManifest } from '@/components/signature/signature-manifest';
import { DocumentStatusBadge, ValiditySemaphore } from '@/components/validity-semaphore';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  IconAlertOctagon,
  IconAlertTriangle,
  IconCheckCircle,
  IconClipboardCheck,
  IconDownload,
  IconEdit,
  IconFile,
  IconHistory,
  IconInfo,
  IconPrinter,
  IconUsers,
  LoadingRegion,
  Skeleton,
  Table,
  Tabs,
  Tooltip,
  cn,
  useToast,
  type TableColumn,
} from '@/design-system';
import { documentsApi, queryKeys, signedActInvalidations, toUserMessage } from '@/lib/api';
import type { ApprovalDecisionEntry, DocumentDetail } from '@/lib/api/types';
import { formatDateOnly, formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';
import { signedActRecordId } from '@/lib/signed-act-record';
import {
  ACCESS_EFFECT_LABEL,
  ACCESS_SCOPE_LABEL,
  ACKNOWLEDGEMENT_STATUS_LABEL,
  APPROVAL_DECISION_LABEL,
  APPROVAL_POLICY_LABEL,
  DOCUMENT_STATUS_LABEL,
  PERMISSION_FLAG_LABEL,
} from '@/lib/i18n';
import { useDirectory } from '@/lib/use-directory';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

type PendingAct =
  | { kind: 'approve' }
  | { kind: 'reject' }
  | { kind: 'obsolete' }
  | { kind: 'acknowledge' };

/** Botão da tela → ato do catálogo `SIGNABLE_ACTS`. */
const SIGNED_ACT_OF: Record<PendingAct['kind'], SignableAct> = {
  approve: 'DOCUMENT_APPROVE',
  reject: 'DOCUMENT_REJECT',
  obsolete: 'DOCUMENT_OBSOLETE',
  acknowledge: 'DOCUMENT_ACKNOWLEDGE',
};

export function DocumentDetailPage() {
  const { documentId } = useParams({ strict: false }) as { documentId: string };
  const [tab, setTab] = useState('dados');
  const [pendingAct, setPendingAct] = useState<PendingAct | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const timeZone = useOrganizationTimeZone();

  /**
   * UMA chamada em vez de cinco.
   *
   * `GET /bff/documents/:id/full` devolve o documento, as versões, o manifesto
   * de assinaturas (§11.50(b), congelado no ato), as decisões, as permissões
   * efetivas, as ciências e a evidência de notificação. Antes esta tela pedia o
   * detalhe e teria de pedir o manifesto de cada versão à parte — o N+1 que o
   * BFF existe para eliminar, e que nunca chegou a ser exercido porque o
   * agregado não era chamado por ninguém.
   */
  const detailQuery = useQuery({
    queryKey: queryKeys.documents.full(documentId),
    queryFn: () => documentsApi.full(documentId),
  });

  const document = detailQuery.data?.document;
  const degraded = detailQuery.data?.degraded ?? [];

  const invalidateAfterSignedAct = async () => {
    await Promise.all(
      signedActInvalidations(documentId).map((key) =>
        queryClient.invalidateQueries({ queryKey: key }),
      ),
    );
  };

  const decide = useMutation({
    mutationFn: async (params: { command: SignedCommand; decision: 'APPROVED' | 'REJECTED' }) => {
      if (!document?.currentVersion) throw new Error('Versão vigente não encontrada.');
      return documentsApi.decide(documentId, document.currentVersion.versionId, {
        ...params.command,
        decision: params.decision,
      });
    },
  });

  const obsolete = useMutation({
    mutationFn: (command: SignedCommand & { reason: string }) =>
      documentsApi.obsolete(documentId, { ...command, reason: command.reason }),
  });

  const acknowledge = useMutation({
    mutationFn: (command: SignedCommand) => {
      if (!document?.currentVersion) throw new Error('Versão vigente não encontrada.');
      return documentsApi.acknowledge(documentId, {
        ...command,
        documentVersionId: document.currentVersion.versionId,
      });
    },
  });

  if (detailQuery.isPending) {
    return (
      <LoadingRegion loading label="Carregando o documento…">
        <div className="p-5">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="mt-3 h-4 w-96" />
          <Skeleton className="mt-6 h-72 w-full" />
        </div>
      </LoadingRegion>
    );
  }

  if (!document) {
    return (
      <PageBody>
        <Card>
          <EmptyState
            icon={<IconFile size={22} />}
            title="Documento não encontrado"
            description="O documento não existe ou você não tem permissão para vê-lo. Registros de outras organizações são sempre tratados como inexistentes."
            action={
              <Button asChild variant="primary">
                <Link to="/documentos">Voltar à Lista Mestra</Link>
              </Button>
            }
          />
        </Card>
      </PageBody>
    );
  }

  const version = document.currentVersion;
  const watermark = version && requiresWatermarkOnExport(version.status)
    ? (WATERMARK_TEXT[version.status] ?? null)
    : null;

  const myAcknowledgement = document.acknowledgements.find(
    (ack) => ack.userId === user?.id && ack.status === AcknowledgementStatus.PENDING,
  );

  /** Ato do catálogo `SIGNABLE_ACTS` correspondente ao botão acionado. */
  const signedAct: SignableAct = pendingAct ? SIGNED_ACT_OF[pendingAct.kind] : 'DOCUMENT_APPROVE';
  const iAmPendingApprover =
    version?.status === DocumentVersionStatus.IN_REVIEW &&
    document.eligibleApproverIds.includes(user?.id ?? '') &&
    !version.decisions.some(
      (decision) => decision.approverUserId === user?.id && decision.decision !== ApprovalDecision.PENDING,
    );

  // O manifesto vem pronto do agregado; o `??` cobre o caso de o BFF ter
  // devolvido o documento sem o bloco (degradação), preservando a tela.
  const allSignatures =
    detailQuery.data?.signatureManifest ?? document.history.flatMap((entry) => entry.signatures);

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Lista Mestra', to: '/documentos' },
          { label: document.name },
        ]}
        title={document.code ? `${document.code} — ${document.name}` : document.name}
        description={document.description || undefined}
        actions={
          <DocumentActions
            document={document}
            iAmPendingApprover={iAmPendingApprover}
            hasPendingAcknowledgement={Boolean(myAcknowledgement)}
            onAct={setPendingAct}
          />
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          {version ? <DocumentStatusBadge status={version.status} /> : null}
          <Badge tone="outline" srPrefix="Versão vigente:">
            Versão {version?.version ?? '—'}
          </Badge>
          <Badge tone="outline" srPrefix="Tipo:">
            {document.documentType}
          </Badge>
          <ValiditySemaphore
            variant="chip"
            state={document.validityState}
            daysUntilExpiry={document.daysUntilExpiry}
            validUntil={version?.validUntil ?? null}
          />
          {watermark ? (
            <Badge tone="danger" icon={<IconAlertOctagon size={12} />}>
              {watermark}
            </Badge>
          ) : null}
        </div>
      </PageHeader>

      <PageBody>
        {/*
          O agregado avisa quando um serviço secundário não respondeu. Omitir
          isso faria o usuário concluir "não há evidência de notificação para
          este documento" quando o correto é "não foi possível verificar" — e
          num sistema regulado essas duas frases não são a mesma coisa.
        */}
        {degraded.length > 0 ? (
          <p
            role="status"
            className="mb-4 flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
          >
            <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Parte das informações não pôde ser carregada agora ({degraded.join(', ')}). Os dados do
              documento e as assinaturas estão completos; a evidência de notificação pode estar
              incompleta nesta visualização.
            </span>
          </p>
        ) : null}

        <Tabs
          ariaLabel="Seções do documento"
          value={tab}
          onValueChange={setTab}
          tabs={[
            {
              value: 'dados',
              label: 'Dados',
              content: <DataTab document={document} />,
            },
            {
              value: 'versoes',
              label: 'Versões',
              count: document.history.length,
              content: <VersionsTab document={document} timeZone={timeZone} />,
            },
            {
              value: 'aprovacoes',
              label: 'Aprovações',
              content: <ApprovalsTab document={document} timeZone={timeZone} />,
            },
            {
              value: 'distribuicao',
              label: 'Distribuição',
              count: document.accessRules.length + document.distribution.length,
              content: <DistributionTab document={document} />,
            },
            {
              value: 'ciencias',
              label: 'Ciências',
              count: document.acknowledgements.length,
              content: <AcknowledgementsTab document={document} timeZone={timeZone} />,
            },
            {
              value: 'auditoria',
              label: 'Trilha de auditoria',
              content: <AuditTabLink documentId={document.id} />,
            },
          ]}
        />

        {/*
          §11.50(b) — o manifesto acompanha o registro em QUALQUER forma legível.
          Aparece aqui na tela e também no bloco de impressão abaixo.
        */}
        <SignatureManifest
          entries={allSignatures}
          className="mt-6"
          recordLabelFallback={`${document.name} v${version?.version ?? ''}`}
        />

        <PrintableRecord
          title={document.code ? `${document.code} — ${document.name}` : document.name}
          subtitle={`Versão ${version?.version ?? '—'} · ${document.documentType}`}
          watermark={watermark}
          metadata={[
            { label: 'Situação', value: version ? DOCUMENT_STATUS_LABEL[version.status] : '—' },
            { label: 'Data da versão', value: formatDateOnly(version?.revisionDate ?? null) },
            { label: 'Válido até', value: formatDateOnly(version?.validUntil ?? null) },
            {
              label: 'Política de aprovação',
              value: APPROVAL_POLICY_LABEL[document.approvalPolicy as ApprovalPolicy],
            },
            { label: 'Justificativa da versão', value: version?.changeJustification ?? '—' },
            {
              label: 'Impressão em',
              value: formatRegulatedDateTime(new Date(), timeZone),
            },
          ]}
          entries={allSignatures}
        />
      </PageBody>

      {/* ── Atos assinados ────────────────────────────────────────────────── */}
      {pendingAct && version ? (
        <SignatureDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingAct(null);
          }}
          act={signedAct}
          /*
           * O `recordId` da PROVA precisa ser exatamente o que o CORE usa no
           * CONSUMO. `SignaturesService.executeSignedAct` compara os dois e,
           * quando divergem, lança `SIGNATURE.PROOF_RECORD_MISMATCH` — depois
           * de já ter queimado a prova (uso único, fail-closed). Antes disto,
           * "Obsoletar" e "Confirmar ciência" pediam a prova com `document.id`
           * e falhavam SEMPRE, obrigando a digitar a senha de novo para falhar
           * igual. A correspondência agora vem de `signedActRecordId`, derivada
           * do `recordType` do catálogo.
           */
          recordId={
            signedActRecordId(signedAct, {
              documentVersionId: version.versionId,
              acknowledgementId: myAcknowledgement?.id ?? null,
            }) ?? ''
          }
          recordLabel={document.code ? `${document.code} — ${document.name}` : document.name}
          recordVersion={version.version}
          recordDetails={[
            { label: 'Tipo', value: document.documentType },
            { label: 'Data da versão', value: formatDateOnly(version.revisionDate) },
            { label: 'Situação atual', value: DOCUMENT_STATUS_LABEL[version.status] },
            { label: 'Impressão digital do arquivo', value: truncateHash(version.fileChecksum) },
          ]}
          onSign={async (command) => {
            if (pendingAct.kind === 'approve') {
              await decide.mutateAsync({ command, decision: 'APPROVED' });
            } else if (pendingAct.kind === 'reject') {
              await decide.mutateAsync({ command, decision: 'REJECTED' });
            } else if (pendingAct.kind === 'obsolete') {
              await obsolete.mutateAsync({ ...command, reason: command.reason ?? '' });
            } else {
              await acknowledge.mutateAsync(command);
            }
          }}
          onSigned={() => {
            setPendingAct(null);
            void invalidateAfterSignedAct();
            toast({
              tone: 'success',
              title: 'Assinatura registrada',
              description:
                'O ato foi gravado junto com a sua assinatura eletrônica na mesma transação. O manifesto abaixo já reflete o registro.',
            });
          }}
        />
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ações
// ─────────────────────────────────────────────────────────────────────────────

/** Texto único para o tooltip e para o leitor de tela — nunca divergem. */
function printHint(canPrint: boolean): string {
  return canPrint
    ? 'A versão de impressão inclui o manifesto de assinaturas e a impressão é registrada na trilha de auditoria.'
    : 'Impressão indisponível: você não tem a permissão "Baixar e imprimir" neste documento. Peça ao gestor da qualidade para revisar as regras de acesso.';
}

function DocumentActions({
  document,
  iAmPendingApprover,
  hasPendingAcknowledgement,
  onAct,
}: {
  readonly document: DocumentDetail;
  readonly iAmPendingApprover: boolean;
  readonly hasPendingAcknowledgement: boolean;
  readonly onAct: (act: PendingAct) => void;
}) {
  const { toast } = useToast();
  const permissions = document.permissions;
  const version = document.currentVersion;
  const [printing, setPrinting] = useState(false);

  const blockedDownload =
    version?.status === DocumentVersionStatus.OBSOLETE ||
    version?.status === DocumentVersionStatus.SUPERSEDED;

  const canPrint = permissions[DocumentPermissionFlag.DOWNLOAD];

  const download = async () => {
    try {
      const ticket = await documentsApi.download(document.id);
      window.open(ticket.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast({ tone: 'error', title: 'Download não autorizado', description: toUserMessage(error) });
    }
  };

  /**
   * IMPRESSÃO AUTORIZADA E AUDITADA — ged.md §15.15 (B9/B10/B11).
   *
   * O legado conferia a permissão e gravava `route='ged/ged/printDocument'` na
   * trilha; esta tela chamava `window.print()` direto, sem checar nada e sem
   * registrar nada. Era a única regressão de trilha do módulo.
   *
   * Agora a impressão só começa depois que o SERVIDOR autoriza: ele reavalia a
   * flag `DOWNLOAD` sobre o estado gravado — a checagem do cliente abaixo é só
   * para não oferecer um botão que vai falhar — e emite o evento `PRINT` antes
   * de devolver o token. Se a autorização falhar, `window.print()` não roda.
   */
  const print = async () => {
    setPrinting(true);
    try {
      const authorization = await documentsApi.printToken(
        document.id,
        version?.versionId,
      );
      if (authorization.watermarkText) {
        toast({
          tone: 'warning',
          title: 'Cópia com marca d’água',
          description: `A versão de impressão sai marcada como “${authorization.watermarkText}”.`,
        });
      }
      // O diálogo do navegador é síncrono e bloqueia a thread; abrir só depois
      // da resposta garante que a trilha já registrou a impressão.
      window.print();
    } catch (error) {
      toast({
        tone: 'error',
        title: 'Impressão não autorizada',
        description: toUserMessage(error),
      });
    } finally {
      setPrinting(false);
    }
  };

  const exportPdf = async () => {
    try {
      const ticket = await documentsApi.exportPdf(document.id);
      window.open(ticket.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast({ tone: 'error', title: 'Exportação indisponível', description: toUserMessage(error) });
    }
  };

  return (
    <>
      {hasPendingAcknowledgement ? (
        <Button
          variant="primary"
          leadingIcon={<IconCheckCircle size={15} />}
          onClick={() => onAct({ kind: 'acknowledge' })}
        >
          Confirmar ciência
        </Button>
      ) : null}

      {iAmPendingApprover && permissions[DocumentPermissionFlag.APPROVE] ? (
        <>
          <Button
            variant="primary"
            leadingIcon={<IconClipboardCheck size={15} />}
            onClick={() => onAct({ kind: 'approve' })}
          >
            Aprovar
          </Button>
          <Button variant="danger-outline" onClick={() => onAct({ kind: 'reject' })}>
            Reprovar
          </Button>
        </>
      ) : null}

      {permissions[DocumentPermissionFlag.DOWNLOAD] ? (
        <Button
          variant="secondary"
          leadingIcon={<IconDownload size={15} />}
          disabled={blockedDownload}
          onClick={() => void download()}
        >
          Baixar arquivo
        </Button>
      ) : null}

      {/*
        O botão continua VISÍVEL sem a permissão, apenas desabilitado e com a
        explicação: esconder faria o usuário procurar uma função que existe e
        que ele não pode usar. O `<span>` recebe o Tooltip porque elemento
        desabilitado não dispara eventos de mouse.
      */}
      <Tooltip content={printHint(canPrint)}>
        <span className="inline-flex">
          <Button
            variant="secondary"
            leadingIcon={<IconPrinter size={15} />}
            disabled={!canPrint}
            loading={printing}
            onClick={() => void print()}
          >
            Imprimir
          </Button>
          {/*
            Botão desabilitado não recebe foco, então o tooltip nunca abre por
            teclado e o motivo ficaria só no hover — WCAG 1.3.1. O mesmo texto
            vai aqui, para o leitor de tela.
          */}
          <span className="qc-sr-only">{printHint(canPrint)}</span>
        </span>
      </Tooltip>

      <Button variant="ghost" onClick={() => void exportPdf()}>
        PDF/A com manifesto
      </Button>

      {permissions[DocumentPermissionFlag.WRITE] ? (
        <Button asChild variant="secondary" leadingIcon={<IconEdit size={15} />}>
          <Link to="/documentos/$documentId/nova-versao" params={{ documentId: document.id }}>
            Nova versão
          </Link>
        </Button>
      ) : null}

      {permissions[DocumentPermissionFlag.SECURITY] &&
      version?.status === DocumentVersionStatus.APPROVED ? (
        <Button variant="danger-outline" onClick={() => onAct({ kind: 'obsolete' })}>
          Obsoletar
        </Button>
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Abas
// ─────────────────────────────────────────────────────────────────────────────

function DataTab({ document }: { readonly document: DocumentDetail }) {
  const version = document.currentVersion;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title="Identificação" headingLevel={3} />
        <dl className="mt-3 grid grid-cols-[11rem_1fr] gap-x-4 gap-y-2 text-sm">
          <DefinitionRow label="Nome" value={document.name} />
          <DefinitionRow label="Código" value={document.code ?? 'Não numerado'} />
          <DefinitionRow label="Tipo" value={document.documentType} />
          <DefinitionRow label="Versão vigente" value={version?.version ?? '—'} />
          <DefinitionRow
            label="Data da versão"
            value={formatDateOnly(version?.revisionDate ?? null)}
          />
          <DefinitionRow label="Descrição" value={document.description || 'Sem descrição'} />
        </dl>
      </Card>

      <Card>
        <CardHeader title="Controle e validade" headingLevel={3} />
        <dl className="mt-3 grid grid-cols-[11rem_1fr] gap-x-4 gap-y-2 text-sm">
          <DefinitionRow
            label="Possui validade"
            value={document.hasValidity ? `Sim — ${document.validityMonths} meses` : 'Não'}
          />
          <DefinitionRow label="Válido até" value={formatDateOnly(version?.validUntil ?? null)} />
          <DefinitionRow
            label="Política de aprovação"
            value={
              document.approvalPolicy === ApprovalPolicy.QUORUM
                ? `${APPROVAL_POLICY_LABEL[ApprovalPolicy.QUORUM]} (${document.quorumSize})`
                : APPROVAL_POLICY_LABEL[document.approvalPolicy as ApprovalPolicy]
            }
          />
          <DefinitionRow
            label="Aprovação automática"
            value={document.autoApprove ? 'Sim' : 'Não'}
          />
          <DefinitionRow
            label="Cópia impressa controlada"
            value={document.hasControlledPrintedCopy ? 'Sim' : 'Não'}
          />
          <DefinitionRow
            label="Exige ciência"
            value={document.requiresAcknowledgement ? 'Sim' : 'Não'}
          />
        </dl>

        {document.obsoletedAt ? (
          <div className="mt-4 rounded-md border border-danger bg-danger-subtle px-3 py-2.5 text-sm text-danger-content">
            <p className="font-semibold">Documento obsoletado</p>
            <p className="mt-0.5">{document.obsoleteReason}</p>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/**
 * Histórico completo de versões: quem, quando e por quê.
 *
 * A justificativa da versão (`changeJustification`) é exibida INTEGRALMENTE.
 * No legado ela era gravada em `erp_document_history.comment` e nunca aparecia
 * em tela nenhuma — a informação existia e era inacessível ao usuário.
 */
function VersionsTab({
  document,
  timeZone,
}: {
  readonly document: DocumentDetail;
  readonly timeZone: string;
}) {
  if (document.history.length === 0) {
    return (
      <EmptyState
        icon={<IconHistory size={22} />}
        title="Sem histórico de versões"
        description="Este documento ainda não possui versões registradas."
      />
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {document.history.map((entry) => (
        <li key={entry.versionId}>
          <Card className={cn(entry.isCurrent && 'border-l-4 border-l-brand')}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 text-base font-semibold text-content">
                  Versão <span className="qc-tabular">{entry.version}</span>
                  {entry.isCurrent ? <Badge tone="brand">Vigente</Badge> : null}
                  <DocumentStatusBadge status={entry.status} />
                </h3>
                <p className="mt-1 text-sm text-content-muted">
                  Elaborada por{' '}
                  <span className="font-medium text-content">
                    {entry.authorName ?? 'Autor não identificado'}
                  </span>{' '}
                  em {formatRegulatedDateTime(entry.createdAt, timeZone)}
                </p>
                {entry.approvedAt ? (
                  <p className="text-sm text-content-muted">
                    Aprovada em {formatRegulatedDateTime(entry.approvedAt, timeZone)}
                  </p>
                ) : null}
              </div>

              <dl className="grid shrink-0 grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                <dt className="text-content-subtle">Data da versão</dt>
                <dd className="text-content qc-tabular">{formatDateOnly(entry.revisionDate)}</dd>
                <dt className="text-content-subtle">Válido até</dt>
                <dd className="text-content qc-tabular">{formatDateOnly(entry.validUntil)}</dd>
                <dt className="text-content-subtle">Arquivo (SHA-256)</dt>
                <dd className="font-mono text-content" title={entry.fileChecksum}>
                  {truncateHash(entry.fileChecksum, 8, 8)}
                </dd>
              </dl>
            </div>

            <div className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                Justificativa da versão
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-content">
                {entry.changeJustification}
              </p>
            </div>

            {entry.signatures.length > 0 ? (
              <SignatureManifest entries={entry.signatures} className="mt-3" />
            ) : null}
          </Card>
        </li>
      ))}
    </ol>
  );
}

/**
 * Aprovações: quem decidiu o quê, com a política vigente e o progresso do
 * quórum. No legado a primeira decisão promovia a versão e cada nova
 * sobrescrevia a anterior (ged.md §7.3, achado C9), e as decisões individuais
 * nunca eram exibidas (BUG-82).
 */
function ApprovalsTab({
  document,
  timeZone,
}: {
  readonly document: DocumentDetail;
  readonly timeZone: string;
}) {
  const summary = document.approvalSummary;
  const decisions = document.currentVersion?.decisions ?? [];

  const columns = useMemo<Array<TableColumn<ApprovalDecisionEntry>>>(
    () => [
      {
        id: 'approver',
        header: 'Aprovador',
        cell: (row) => (
          <div>
            <span className="font-medium text-content">
              {row.approverName ?? 'Usuário não identificado'}
            </span>
            {!row.stillEligible ? (
              <Badge tone="warning" size="sm" className="ml-2">
                Não é mais aprovador
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'decision',
        header: 'Decisão',
        cell: (row) => (
          <Badge
            tone={
              row.decision === ApprovalDecision.APPROVED
                ? 'success'
                : row.decision === ApprovalDecision.REJECTED
                  ? 'danger'
                  : 'neutral'
            }
            srPrefix="Decisão:"
          >
            {APPROVAL_DECISION_LABEL[row.decision]}
          </Badge>
        ),
      },
      {
        id: 'decidedAt',
        header: 'Data e hora',
        cell: (row) => (
          <span className="qc-tabular text-content-muted">
            {row.decidedAt ? formatRegulatedDateTime(row.decidedAt, timeZone) : '—'}
          </span>
        ),
      },
      {
        id: 'comment',
        header: 'Justificativa',
        cell: (row) => <span className="text-content-muted">{row.comment ?? '—'}</span>,
      },
      {
        id: 'signature',
        header: 'Assinatura',
        cell: (row) =>
          row.signatureId ? (
            <span className="font-mono text-xs text-content-subtle" title={row.signatureId}>
              {truncateHash(row.signatureId, 6, 4)}
            </span>
          ) : (
            <span className="text-content-subtle">—</span>
          ),
      },
    ],
    [timeZone],
  );

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          headingLevel={3}
          title="Política de aprovação"
          description="Regra que determina quando a versão pode ser promovida."
        />
        <dl className="mt-3 grid grid-cols-[13rem_1fr] gap-x-4 gap-y-2 text-sm">
          <DefinitionRow
            label="Política"
            value={
              document.approvalPolicy === ApprovalPolicy.QUORUM
                ? `${APPROVAL_POLICY_LABEL[ApprovalPolicy.QUORUM]} — ${document.quorumSize} aprovações`
                : APPROVAL_POLICY_LABEL[document.approvalPolicy as ApprovalPolicy]
            }
          />
          <DefinitionRow
            label="Aprovadores designados"
            value={String(document.eligibleApproverIds.length)}
          />
        </dl>

        {summary ? (
          <div className="mt-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium text-content">Progresso do quórum</p>
              <p className="text-sm text-content-muted qc-tabular">
                {summary.approvals} de {summary.required} aprovações
                {summary.rejections > 0 ? ` · ${summary.rejections} reprovação(ões)` : ''}
              </p>
            </div>
            <div
              role="progressbar"
              aria-valuenow={summary.approvals}
              aria-valuemin={0}
              aria-valuemax={summary.required}
              aria-valuetext={`${summary.approvals} de ${summary.required} aprovações registradas`}
              aria-label="Progresso das aprovações"
              className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-surface-sunken"
            >
              <div
                className={cn(
                  'h-full rounded-full',
                  summary.rejections > 0 ? 'bg-danger' : 'bg-success',
                )}
                style={{
                  width: `${summary.required === 0 ? 0 : Math.min(100, (summary.approvals / summary.required) * 100)}%`,
                }}
              />
            </div>
            {summary.pendingApproverIds.length > 0 ? (
              <p className="mt-2 text-sm text-content-muted">
                Aguardando decisão de {summary.pendingApproverIds.length}{' '}
                {summary.pendingApproverIds.length === 1 ? 'aprovador' : 'aprovadores'}.
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Table<ApprovalDecisionEntry>
        caption="Decisões de aprovação da versão vigente, com autor, decisão, data e assinatura."
        columns={columns}
        rows={decisions}
        rowKey={(row) => row.approverUserId}
        density="comfortable"
        empty={
          <EmptyState
            variant="compact"
            icon={<IconClipboardCheck size={20} />}
            title="Nenhuma decisão registrada"
            description="A versão vigente ainda não recebeu decisões dos aprovadores designados."
          />
        }
      />
    </div>
  );
}

/**
 * Distribuição — DOIS conceitos na mesma aba, deliberadamente separados.
 *
 *   Regras de acesso        → quem PODE ver, editar, aprovar   (ACL)
 *   Distribuição controlada → quem PRECISA confirmar leitura   (ciência)
 *
 * No legado a aba "Distribuição" era só a ACL (ged.md §13.1) e listava as
 * linhas cruas de permissão, com o efeito combinado invisível; confirmação de
 * leitura não existia (§13.2, BUG-80).
 *
 * A EDIÇÃO usa `PATCH /documents/:id/distribution`, que altera ACL e
 * distribuição SEM gerar nova versão — a regra R36 do legado (`ged.php:280`).
 * A rota existia, estava testada e nenhuma tela a chamava; era a única forma de
 * exercer a regra e ela não tinha botão.
 */
function DistributionTab({ document }: { readonly document: DocumentDetail }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const directory = useDirectory();
  const subjects = directory.subjects;

  const [editing, setEditing] = useState(false);
  /*
   * Esta tela passou a editar as REGRAS, e não mais uma distribuição separada:
   * a exigência de ciência virou um check da regra que concede a leitura. O
   * endpoint é o mesmo — `updateDistributionSchema` sempre recebeu `accessRules`
   * —, mas agora elas são editáveis aqui, em vez de reenviadas iguais.
   */
  const [draftRules, setDraftRules] = useState<AccessRuleInput[]>([]);
  const [draftUserIds, setDraftUserIds] = useState<string[]>([]);
  const [draftDepartmentIds, setDraftDepartmentIds] = useState<string[]>([]);

  const currentUserIds = useMemo(
    () =>
      document.distribution
        .filter((entry) => entry.userId !== null)
        .map((entry) => entry.userId as string),
    [document.distribution],
  );
  const currentDepartmentIds = useMemo(
    () =>
      document.distribution
        .filter((entry) => entry.departmentId !== null)
        .map((entry) => entry.departmentId as string),
    [document.distribution],
  );

  const save = useMutation({
    mutationFn: () =>
      documentsApi.updateDistribution(document.id, {
        accessRules: draftRules,
        distributionUserIds: draftUserIds,
        distributionDepartmentIds: draftDepartmentIds,
        // Derivada: existe destinatário, existe exigência. Deixou de ser um
        // interruptor à parte, e com isso a incoerência "exigência ligada sem
        // ninguém para alcançar" deixou de ser possível.
        requiresAcknowledgement: draftUserIds.length > 0 || draftDepartmentIds.length > 0,
      }),
    onSuccess: async () => {
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.documents.all() });
      toast({
        tone: 'success',
        title: 'Distribuição atualizada',
        description:
          'Alterar apenas a distribuição não gera nova versão do documento. A alteração foi registrada na trilha de auditoria.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível salvar a distribuição',
        description: toUserMessage(error),
      }),
  });

  const startEditing = () => {
    setDraftRules(
      document.accessRules.map((rule) => ({
        scope: rule.scope as never,
        effect: rule.effect as never,
        userId: rule.userId,
        departmentId: rule.departmentId,
        flags: rule.flags as never,
      })),
    );
    setDraftUserIds(currentUserIds);
    setDraftDepartmentIds(currentDepartmentIds);
    setEditing(true);
  };

  const canManage = document.permissions[DocumentPermissionFlag.SECURITY];

  return (
    <div className="flex flex-col gap-5">
      <Card padding="none">
        <div className="p-4">
          <CardHeader
            headingLevel={3}
            title={`Regras de acesso (${document.accessRules.length})`}
            description="Quem PODE ver, editar, baixar, aprovar ou administrar. Regras cadastradas em três alcances; a permissão efetiva de cada colaborador é o resultado combinado delas."
          />
        </div>

        {document.accessRules.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<IconUsers size={20} />}
            title="Nenhuma regra de acesso"
            description="Sem regras, nenhum colaborador enxerga este documento na Lista Mestra."
          />
        ) : (
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full border-collapse text-sm">
              <caption className="qc-sr-only">
                Regras de acesso do documento, com alcance, efeito e permissões cobertas.
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                    Alcance
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                    Efeito
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                    Permissões
                  </th>
                </tr>
              </thead>
              <tbody>
                {document.accessRules.map((rule, index) => (
                  <tr key={index} className="border-b border-border-subtle last:border-b-0">
                    <td className="px-3 py-2 text-content">
                      {ACCESS_SCOPE_LABEL[rule.scope as keyof typeof ACCESS_SCOPE_LABEL] ?? rule.scope}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        tone={rule.effect === 'DENY' ? 'danger' : 'success'}
                        srPrefix="Efeito:"
                      >
                        {ACCESS_EFFECT_LABEL[rule.effect as keyof typeof ACCESS_EFFECT_LABEL] ??
                          rule.effect}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <ul className="flex flex-wrap gap-1">
                        {rule.flags.map((flag) => (
                          <li key={flag}>
                            <Badge tone="outline" size="sm">
                              {PERMISSION_FLAG_LABEL[flag]}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/*
        A tabela "Permissão efetiva por colaborador" saiu: era uma grade de seis
        colunas por pessoa, e numa organização de trinta ocupava a tela inteira.
        O que NÃO podia sair com ela são os avisos de falha silenciosa —
        documento que ninguém enxerga, versão sem aprovador, quórum impossível.
        Mesma função pura, mesmos testes; muda só onde o resultado aparece.
      */}
      <ConfigurationWarnings
        rules={(editing ? draftRules : document.accessRules.map((rule) => ({
          scope: rule.scope as never,
          effect: rule.effect as never,
          userId: rule.userId,
          departmentId: rule.departmentId,
          flags: rule.flags as never,
        })))}
        subjects={subjects}
        approvalPolicy={document.approvalPolicy}
        quorumSize={document.quorumSize}
      />

      {/* ── Distribuição controlada (ciência) ─────────────────────────── */}
      {editing ? (
        <div className="flex flex-col gap-3">
          <AccessRuleEditor
            rules={draftRules}
            onChange={setDraftRules}
            distributionUserIds={draftUserIds}
            distributionDepartmentIds={draftDepartmentIds}
            onDistributionChange={(userIds, departmentIds) => {
              setDraftUserIds(userIds);
              setDraftDepartmentIds(departmentIds);
            }}
            users={subjects.map((subject) => ({
              id: subject.userId,
              name: subject.displayName,
              hint: subject.jobTitle,
            }))}
            departments={directory.departments}
            disabled={save.isPending}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" disabled={save.isPending} onClick={() => setEditing(false)}>
              Cancelar
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Salvar distribuição
            </Button>
          </div>
        </div>
      ) : (
        <Card>
          <CardHeader
            headingLevel={3}
            title={`Distribuição controlada (${document.distribution.length})`}
            description="Quem PRECISA confirmar a leitura da versão aprovada. Alterar somente a distribuição não gera nova versão do documento."
            action={
              canManage ? (
                <Button variant="secondary" leadingIcon={<IconEdit size={15} />} onClick={startEditing}>
                  Editar distribuição
                </Button>
              ) : undefined
            }
          />

          <div className="mt-4">
            {document.distribution.length === 0 ? (
              <div className="rounded-md border border-border bg-surface-sunken px-3 py-2.5">
                <p className="text-sm font-medium text-content">Nenhum destinatário definido</p>
                <p className="mt-1 text-sm text-content-muted">
                  {document.requiresAcknowledgement
                    ? 'A exigência de ciência está ligada e não alcança ninguém: ao aprovar a versão, nenhuma pendência será criada. Edite a distribuição para escolher os destinatários.'
                    : 'Este documento não exige confirmação de leitura. Se precisar, edite a distribuição para escolher quem deve dar ciência.'}
                </p>
              </div>
            ) : (
              <ul
                aria-label="Destinatários da distribuição controlada"
                className="flex flex-wrap gap-1.5"
              >
                {document.distribution.map((entry, index) => (
                  <li key={`${entry.scope}-${entry.userId ?? entry.departmentId ?? index}`}>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-2.5 py-0.5 text-sm text-content">
                      <Badge tone="outline" size="sm">
                        {ACCESS_SCOPE_LABEL[entry.scope as keyof typeof ACCESS_SCOPE_LABEL] ??
                          entry.scope}
                      </Badge>
                      {describeDistributionTarget(entry, subjects, directory.departments)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <p className="mt-3 flex items-start gap-2 text-sm text-content-muted">
              <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                Exigência de ciência:{' '}
                <strong className="font-semibold text-content">
                  {document.requiresAcknowledgement ? 'ativa' : 'inativa'}
                </strong>
                . A confirmação é um ato assinado eletronicamente (ISO 13485 §4.2.4) e aparece na
                aba Ciências.
              </span>
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

function describeDistributionTarget(
  entry: DocumentDetail['distribution'][number],
  subjects: ReadonlyArray<{ readonly userId: string; readonly displayName: string }>,
  departments: ReadonlyArray<{ readonly id: string; readonly name: string }>,
): string {
  if (entry.userId) {
    return (
      subjects.find((subject) => subject.userId === entry.userId)?.displayName ??
      'Colaborador removido'
    );
  }
  if (entry.departmentId) {
    return (
      departments.find((department) => department.id === entry.departmentId)?.name ??
      'Departamento removido'
    );
  }
  return 'Toda a organização';
}

function AcknowledgementsTab({
  document,
  timeZone,
}: {
  readonly document: DocumentDetail;
  readonly timeZone: string;
}) {
  if (document.acknowledgements.length === 0) {
    return (
      <EmptyState
        icon={<IconCheckCircle size={22} />}
        title="Nenhuma ciência registrada"
        description="Este documento não exige confirmação de leitura, ou a versão vigente ainda não foi distribuída."
      />
    );
  }

  return (
    <Table
      caption="Ciências dos colaboradores sobre a versão vigente do documento."
      columns={[
        {
          id: 'user',
          header: 'Colaborador',
          cell: (row: DocumentDetail['acknowledgements'][number]) => (
            <span className="font-medium text-content">{row.userName ?? row.userId}</span>
          ),
        },
        {
          id: 'status',
          header: 'Situação',
          cell: (row: DocumentDetail['acknowledgements'][number]) => (
            <Badge
              tone={
                row.status === AcknowledgementStatus.ACKNOWLEDGED
                  ? 'success'
                  : row.status === AcknowledgementStatus.PENDING
                    ? 'warning'
                    : 'neutral'
              }
              srPrefix="Situação da ciência:"
            >
              {ACKNOWLEDGEMENT_STATUS_LABEL[row.status]}
            </Badge>
          ),
        },
        {
          id: 'dueAt',
          header: 'Prazo',
          cell: (row: DocumentDetail['acknowledgements'][number]) => (
            <span className="qc-tabular text-content-muted">{formatDateOnly(row.dueAt)}</span>
          ),
        },
        {
          id: 'acknowledgedAt',
          header: 'Confirmada em',
          cell: (row: DocumentDetail['acknowledgements'][number]) => (
            <span className="qc-tabular text-content-muted">
              {row.acknowledgedAt ? formatRegulatedDateTime(row.acknowledgedAt, timeZone) : '—'}
            </span>
          ),
        },
      ]}
      rows={[...document.acknowledgements]}
      rowKey={(row) => row.userId}
      density="comfortable"
    />
  );
}

function AuditTabLink({ documentId }: { readonly documentId: string }) {
  return (
    <Card>
      <CardHeader
        headingLevel={3}
        title="Trilha de auditoria deste documento"
        description="Todos os eventos registrados para este registro, em banco de dados separado, sem permissão de alteração nem exclusão para ninguém — nem para o administrador da aplicação."
      />
      <div className="mt-4">
        <Button asChild variant="primary" leadingIcon={<IconHistory size={15} />}>
          <Link to="/auditoria" search={{ entityId: documentId, entityType: 'Document', page: 1 }}>
            Abrir a trilha filtrada por este documento
          </Link>
        </Button>
      </div>
    </Card>
  );
}

function DefinitionRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <>
      <dt className="text-content-subtle">{label}</dt>
      <dd className="min-w-0 break-words text-content">{value}</dd>
    </>
  );
}
